/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useUI } from "../../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import clsx from "clsx";
import Swal from "sweetalert2";

export default function ManualOrdersPage() {
    const { lang, theme } = useUI();
    const isArabic = lang === "ar";
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";
    const tenantId = parseInt(localStorage.getItem("tenantId") || "-1");

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    const parseOrderDate = (value) => {
        if (!value) return "-";
        // Handle the specific format from API: "Jan 25 2026 2:54PM" -> "Jan 25 2026 2:54 PM"
        const fixed = value.toString().replace(/\s+/g, " ").replace(/(\d)(AM|PM)/i, "$1 $2");
        const d = Date.parse(fixed);
        if (!isNaN(d)) {
            return new Date(d).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        }
        return value;
    };

    // Fetch Orders
    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const token = localStorage.getItem("authToken");

                // Params: tenantId, fromDate, toDate, statusId, OrderNo, CustomerId
                // We'll fetch all (-1) and filter client side
                const query = `tenantId=${tenantId}&statusId=-1&OrderNo=-1&CustomerId=-1`;
                const res = await fetch(`${baseUrl}/GetTenantOrders?${query}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });

                if (res.ok) {
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        // Filter for manual orders only
                        const manualOrders = data.filter(o =>
                            o.isManual === 1 ||
                            o.isManual === true ||
                            (o.transactionId && o.transactionId.toLowerCase() === "manual")
                        );
                        setOrders(manualOrders);
                    }
                }
            } catch (err) {
                console.error("Error fetching manual orders:", err);
            } finally {
                setLoading(false);
            }
        };

        if (tenantId !== -1) {
            fetchOrders();
        }
    }, [baseUrl, tenantId]);


    const handleDelete = async (orderId) => {
        const result = await Swal.fire({
            title: isArabic ? "هل أنت متأكد؟" : "Are you sure?",
            text: isArabic ? "لن تتمكن من استرجاع هذا الطلب!" : "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: isArabic ? "نعم، احذفه!" : "Yes, delete it!",
            cancelButtonText: isArabic ? "إلغاء" : "Cancel"
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                const res = await fetch(`${baseUrl}/DeleteTenantOrders?tenantId=${tenantId}&orderId=${orderId}`, {
                    method: "DELETE",
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });

                if (res.ok) {
                    setOrders(prev => prev.filter(o => String(o.orderId) !== String(orderId)));
                    Swal.fire(
                        isArabic ? "تم الحذف!" : "Deleted!",
                        isArabic ? "تم حذف الطلب بنجاح." : "Order has been deleted.",
                        "success"
                    );
                } else {
                    throw new Error("Failed to delete");
                }
            } catch (err) {
                Swal.fire(
                    isArabic ? "خطأ!" : "Error!",
                    isArabic ? "حدث خطأ أثناء الحذف." : "Something went wrong.",
                    "error"
                );
            }
        }
    };

    return (
        <PageContainer title={isArabic ? "الطلبات اليدوية" : "Manual Orders"}>
            {/* Top Action Bar */}
            <div className="flex justify-end mb-6">
                <button
                    onClick={() => navigate("/admin-store/orders/create?isManual=1")}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-medium transition-all shadow-lg "
                    style={{ backgroundColor: "#5B2C6F" }} // Dark purple color from screenshot
                >
                    <FiPlus size={20} />
                    <span>{isArabic ? "إنشاء" : "Create"}</span>
                </button>
            </div>

            {/* Table */}

            <ResponsiveTable
                columns={[
                    { header: isArabic ? "رقم الطلب اليدوي" : "Manual Order ID", align: "center" },
                    { header: isArabic ? "اسم العميل" : "Customer Name", align: "center" },
                    { header: isArabic ? "تاريخ الطلب" : "Order Date", align: "center" },
                    { header: isArabic ? "الإجمالي" : "Total", align: "center" },
                    { header: isArabic ? "الإجراءات" : "Actions", align: "center" },
                ]}
                data={orders}
                isLoading={loading}
                renderRow={(order) => {
                    // حساب مجموع الـ lineTotal من داخل مصفوفة details
                    const calculatedTotal = order.details
                        ? order.details.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0)
                        : 0;

                    return (
                        <>
                            {/* 1. رقم الطلب اليدوي */}
                            <td style={{ textAlign: 'center', padding: '16px' }}>
                                <span className="text-purple-600 font-bold text-base">#{order.orderNo || "---"}</span>
                            </td>

                            {/* 2. اسم العميل */}
                            <td style={{ textAlign: 'center', padding: '16px' }}>
                                <span className="text-gray-900 dark:text-white font-bold text-sm">
                                    {order.customerName || "---"}
                                </span>
                            </td>

                            {/* 3. تاريخ الطلب */}
                            <td style={{ textAlign: 'center', padding: '16px' }}>
                                <span className="text-gray-500 text-xs">
                                    {parseOrderDate(order.orderDate)}
                                </span>
                            </td>

                            {/* 4. الإجمالي */}
                            <td style={{ textAlign: 'center', padding: '16px' }}>
                                <div className="flex flex-col items-center justify-center">
                                    <span className="font-black text-gray-900 dark:text-white text-base">
                                        {(calculatedTotal || Number(order.netAmount || 0)).toLocaleString()}
                                    </span>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase">
                                        {isArabic ? "د.ع" : "IQD"}
                                    </span>
                                </div>
                            </td>

                            {/* 5. الإجراءات */}
                            <td style={{ textAlign: 'center', padding: '16px' }}>
                                <div className="flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => navigate(`/admin-store/orders/create?isManual=1&id=${order.orderId}`)}
                                        className="px-5 py-2 rounded-xl text-white text-xs font-bold transition-all active:brightness-110 shadow-sm"
                                        style={{ backgroundColor: "#5B2C6F" }}
                                    >
                                        {isArabic ? "تعديل" : "Edit"}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(order.orderId)}
                                        className={clsx(
                                            "px-5 py-2 rounded-xl text-xs font-bold transition-all",
                                            isDark
                                                ? "bg-red-500/10 text-red-400 "
                                                : "bg-red-50 text-red-600 "
                                        )}
                                    >
                                        {isArabic ? "حذف" : "Delete"}
                                    </button>
                                </div>
                            </td>
                        </>
                    );
                }}
            />
        </PageContainer>
    );
}
